<template>
  <div v-if="!editing" class="tw:space-y-1">
    <template v-for="(entry, index) in visibleEntries" :key="entry.id">
      <SidebarLink
        v-if="entry.kind === 'link'"
        :link="entry"
        :organization-id="organizationId"
        :variant="variant"
        :is-pinned="isPinned"
        :active="entry.id === activeLinkId"
        :editing="false"
        @navigate="$emit('navigate')"
      />
      <div v-else class="tw:space-y-1">
        <!--
          Spacing matches the original fixed sidebar to the pixel: with the 24px header (16px of
          text, centered), the title sits 8px below this rule and 8px above the first item on
          desktop, 12px and 8px on mobile. The top margin collapses with the 4px gap above.
        -->
        <div
          v-if="(index > 0 || hasItemsAbove) && !collapsedDesktop"
          class="tw:border-t tw:border-slate-800/40 tw:mx-1"
          :class="variant === 'desktop' ? 'tw:mt-3 tw:mb-1' : 'tw:mt-4 tw:mb-2'"
        ></div>
        <SidebarSectionHeader
          :section="entry"
          :variant="variant"
          :is-pinned="isPinned"
          :editing="false"
          :renaming="false"
          :contains-active="entry.links.some(link => link.id === activeLinkId)"
          @toggle="controller.toggleSection(entry.id, !entry.section.collapsed)"
        />
        <template v-if="!entry.section.collapsed">
          <SidebarLink
            v-for="link in entry.links"
            :key="link.id"
            :link="link"
            :organization-id="organizationId"
            :variant="variant"
            :is-pinned="isPinned"
            :active="link.id === activeLinkId"
            :editing="false"
            @navigate="$emit('navigate')"
          />
        </template>
      </div>
    </template>
  </div>

  <div v-else class="sidebar-tree-editing">
    <draggable
      :model-value="controller.entries.value"
      item-key="id"
      handle=".drag-handle"
      :group="{ name: 'sidebar', pull: true, put: true }"
      :animation="150"
      ghost-class="sidebar-drag-ghost"
      class="tw:space-y-1 tw:min-h-4"
      @update:model-value="controller.reorderTopLevel"
    >
      <template #item="{ element: entry }">
        <SidebarLink
          v-if="entry.kind === 'link'"
          :link="entry"
          :organization-id="organizationId"
          variant="desktop"
          :is-pinned="true"
          :active="false"
          :editing="true"
          @edit="editLink(entry)"
          @remove="controller.removeLink(entry.id)"
        />
        <div v-else data-kind="section" class="tw:rounded-xl tw:border tw:border-dashed tw:border-slate-800 tw:p-1 tw:mt-2">
          <SidebarSectionHeader
            :section="entry"
            variant="desktop"
            :is-pinned="true"
            :editing="true"
            :renaming="renamingSectionId === entry.id"
            :contains-active="false"
            @start-rename="renamingSectionId = entry.id"
            @rename="title => { controller.renameSection(entry.id, title); renamingSectionId = null }"
            @cancel-rename="renamingSectionId = null"
            @delete="deleteSection(entry)"
          />
          <draggable
            :model-value="entry.links"
            item-key="id"
            handle=".drag-handle"
            :group="{ name: 'sidebar', pull: true, put: acceptsIntoSection }"
            :animation="150"
            ghost-class="sidebar-drag-ghost"
            class="tw:space-y-1 tw:min-h-9"
            @update:model-value="(links: ResolvedSidebarLink[]) => controller.reorderSection(entry.id, links)"
          >
            <template #item="{ element: link }">
              <SidebarLink
                :link="link"
                :organization-id="organizationId"
                variant="desktop"
                :is-pinned="true"
                :active="false"
                :editing="true"
                @edit="editLink(link)"
                @remove="controller.removeLink(link.id)"
              />
            </template>
            <template #footer>
              <div>
                <button
                  type="button"
                  class="sidebar-add-button tw:w-full tw:flex tw:items-center tw:gap-2 tw:h-9 tw:px-3 tw:rounded-lg tw:text-sm tw:text-indigo-500 tw:hover:bg-slate-800/90 tw:cursor-pointer"
                  @click="addItem(entry.id, entry.section.title)"
                >
                  <Plus class="tw:w-4 tw:h-4" />
                  <span>{{ entry.links.length ? 'Add item' : 'Add an item, or drag one here' }}</span>
                </button>
                <!--
                  Otherwise a section added and left empty vanishes on Done and looks unsaved. Only
                  for a truly empty section: one whose items are all hidden by access is a different
                  case, and saying this there would be misleading.
                -->
                <p v-if="entry.section.items.length === 0" class="tw:m-0 tw:px-3 tw:pb-1 tw:text-xs tw:text-slate-500">
                  Empty sections are hidden outside Customize mode.
                </p>
              </div>
            </template>
          </draggable>
        </div>
      </template>
    </draggable>

    <div class="tw:mt-3 tw:pt-3 tw:border-t tw:border-slate-800/40 tw:space-y-1">
      <button
        type="button"
        class="sidebar-add-button tw:w-full tw:flex tw:items-center tw:gap-2 tw:h-9 tw:px-3 tw:rounded-lg tw:text-sm tw:text-indigo-500 tw:hover:bg-slate-800/90 tw:cursor-pointer"
        @click="addItem(null, null)"
      >
        <Plus class="tw:w-4 tw:h-4" />
        <span>Add item</span>
      </button>
      <button
        type="button"
        class="sidebar-add-button tw:w-full tw:flex tw:items-center tw:gap-2 tw:h-9 tw:px-3 tw:rounded-lg tw:text-sm tw:text-indigo-500 tw:hover:bg-slate-800/90 tw:cursor-pointer"
        @click="addSection"
      >
        <FolderPlus class="tw:w-4 tw:h-4" />
        <span>Add section</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useDialog } from 'primevue/usedialog'
import draggable from 'vuedraggable'
import { FolderPlus, Plus } from '@lucide/vue'
import SidebarLink from './SidebarLink.vue'
import SidebarSectionHeader from './SidebarSectionHeader.vue'
import { openAddSidebarItem } from './add-sidebar-item-dialog'
import { editSidebarLink } from './edit-sidebar-link-dialog'
import { promptDeleteSidebarSection } from './delete-sidebar-section-dialog'
import type { ResolvedSidebarLink, ResolvedSidebarSection, SidebarLayoutController } from './useSidebarLayout'

const props = defineProps<{
  controller: SidebarLayoutController
  variant: 'desktop' | 'mobile'
  isPinned: boolean
  editing: boolean
  activeLinkId: string | null
  organizationId: string | number | null
  /** Items drawn above this tree (the fixed Overview), so even the first section needs its rule. */
  hasItemsAbove: boolean
}>()

defineEmits<{
  navigate: []
}>()

const router = useRouter()
const store = useStore()
const dialog = useDialog()

const renamingSectionId = ref<string | null>(null)

const collapsedDesktop = computed(() => props.variant === 'desktop' && !props.isPinned)

// Outside Customize mode a section with nothing the person can open would be a bare heading.
const visibleEntries = computed(() => props.controller.entries.value.filter(entry => entry.kind === 'link' || entry.links.length > 0))

// Sections are one level deep: a section can be dropped among top-level entries, never into another section.
const acceptsIntoSection = (_to: unknown, _from: unknown, dragEl: HTMLElement) => dragEl.dataset.kind !== 'section'

function addItem (sectionId: string | null, sectionTitle: string | null) {
  openAddSidebarItem(dialog, { controller: props.controller, router, store, sectionId, sectionTitle })
}

function addSection () {
  renamingSectionId.value = props.controller.addSection()
}

function editLink (link: ResolvedSidebarLink) {
  editSidebarLink(dialog, props.controller, link)
}

async function deleteSection (entry: ResolvedSidebarSection) {
  const storedCount = entry.section.items.length
  if (storedCount === 0) {
    props.controller.removeSection(entry.id, false)
    return
  }
  const choice = await promptDeleteSidebarSection(dialog, {
    title: entry.section.title,
    visibleCount: entry.links.length,
    hiddenCount: storedCount - entry.links.length
  })
  if (choice) {
    props.controller.removeSection(entry.id, choice === 'keep')
  }
}
</script>

<style scoped>
.sidebar-tree-editing :deep(.sidebar-drag-ghost) {
  opacity: 0.4;
}
</style>
